import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IncidentCodeService } from '@shared/services/incident-code-service';
import { EmployeeService } from '@shared/services/mesuser.service';
import { NotificationService } from '@shared/services/notification.service';
import { IPutStockReorderRequest, IStockReorder, StockReorderService } from '@shared/services/stock-reorder.service';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { forkJoin, Subject, take, takeUntil } from 'rxjs';
import { IBatch, MesBatchService } from '../mes-batch/mes-batch-service';
import { DeleteStockReorderComponent } from './delete-stock-reorder/delete-stock-reorder.component';
import { UpdateStockReorderComponent } from './update-stock-reorder/update-stock-reorder.component';

interface IStockReorderView {
	batch: string;
	terminal: string;
	title: string;
	reorderPartNumber: string;
	handled: boolean;
	stockReorder: IStockReorder;
}

@Component({
	selector: 'app-stock-reorder',
	templateUrl: './stock-reorder.component.html',
	styleUrl: './stock-reorder.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class StockReorderComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	batches: Map<string, IBatch>;
	terminals: Map<string, ITerminal>;
	displayedColumns: string[] = ['title', 'batch', 'terminal', 'reorderPartNumber', 'handled', 'menu'];

	dataSource = new MatTableDataSource<IStockReorderView>();
	showSearch = false;
	isLoading = false;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private terminalService: TerminalAdministrationService,
		private batchService: MesBatchService,
		private stockReorderService: StockReorderService,
		private employeeService: EmployeeService,
		private incidentCodeService: IncidentCodeService,
	) {}

	ngOnInit() {
		this.terminalService
			.getTerminals()
			.pipe(take(1))
			.subscribe((terminals) => {
				this.terminals = new Map(terminals.result.map((terminal) => [terminal.id, terminal]));
				this.initializeList();
			});

		this.batchService
			.getAll()
			.pipe(take(1))
			.subscribe((batches) => {
				this.batches = new Map(batches.result.map((batch) => [batch.id, batch]));
				this.initializeList();
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.initializeList();
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'batch', start: 'asc', disableClear: true });
		}
	}

	private getTableViewModel(stockReorders: IStockReorder[]): IStockReorderView[] {
		return (
			stockReorders?.map((stockReorder) => {
				return {
					stockReorder,
					batch: this.batches.get(stockReorder.batchId)?.alias || '',
					terminal: this.terminals.get(stockReorder.terminalId)?.alias || '',
					title: stockReorder.title,
					reorderPartNumber: stockReorder.reorderPartNumber,
					handled: stockReorder.handled,
					incidentCodeId: stockReorder.incidentCodeId,
				};
			}) ?? []
		);
	}

	updateViewModel(stockReorder: IStockReorder) {
		if (!stockReorder || !this.terminals || !this.batches) {
			return;
		}

		const item = this.getTableViewModel([stockReorder])[0];
		const idx = this.dataSource.data.findIndex((x) => x.stockReorder.id === item.stockReorder.id);
		if (idx > -1) {
			this.dataSource.data[idx] = item;
		} else {
			this.dataSource.data.push(item);
		}
		this.dataSource.data = [...this.dataSource.data];
	}

	deleteViewModel(id: string): void {
		if (!id || this.dataSource?.data?.length) {
			return;
		}

		this.dataSource.data = this.dataSource.data.filter((x) => x.stockReorder.id !== id);
	}

	toggleHandled(stockReorder: IStockReorder) {
		stockReorder.handled = !stockReorder.handled;
		const putRequest: IPutStockReorderRequest = {
			handled: stockReorder.handled,
			incidentCodeId: stockReorder.incidentCodeId,
			reorderPartNumber: stockReorder.reorderPartNumber,
			title: stockReorder.title,
		};
		this.stockReorderService.updateStockReorder(stockReorder.id, putRequest).subscribe({
			next: () => {
				this.initializeList();
				this.notificationService.notify(['mes.stock-reorder.notifications.update-handled.header'], [`mes.stock-reorder.notifications.update-handled.content.${stockReorder.handled ? 'handled' : 'unhandled'}`], 'success');
			},
		});
	}

	initializeList() {
		if (this.isLoading || !this.terminals || !this.batches) {
			return;
		}

		this.isLoading = true;
		this.stockReorderService.getAll().subscribe((c) => {
			this.dataSource.data = this.getTableViewModel(c);
			this.dataSource.sort = this.sort;
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	openEdit(item: IStockReorder | null) {
		// Fetch employees and incident codes before opening dialog
		forkJoin({
			employees: this.employeeService.getEmployees().pipe(take(1)),
			incidentCodes: this.incidentCodeService.getAll().pipe(take(1)),
		}).subscribe(({ employees, incidentCodes }) => {
			const incidentCodesByGroup = this.incidentCodeService.groupIncidentCodes(incidentCodes);

			const dialogRef = this.dialog.open(UpdateStockReorderComponent, {
				disableClose: true,
				width: '1200px',
				data: {
					...item,
					employees: employees.result.filter((e) => e.isActive),
					incidentCodesByGroup,
				},
			});
			dialogRef.componentInstance.updated.pipe(takeUntil(this.destroy$)).subscribe(() => {
				this.initializeList(); // TODO - skift til GraphQl
				this.notificationService.notify(['mes.stock-reorder.notifications.update.header'], ['mes.stock-reorder.notifications.update.content', { title: item.title }], 'success');
			});
		});
	}

	openDelete(item: IStockReorder) {
		const dialogRef = this.dialog.open(DeleteStockReorderComponent, {
			disableClose: true,
			width: '450px',
			data: item,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.initializeList(); // TODO - skift til GraphQl
			this.notificationService.notify(['mes.stock-reorder.notifications.delete.header'], ['mes.stock-reorder.notifications.delete.content', { title: item.title }], 'success');
		});
	}
}
